// Mutable app state with a tiny pub-sub. Subscribers re-render when filters/data change.

import {
  TAG_CONFIDENCE_THRESHOLD,
  classifyAI,
  classifyLLMAI,
  llmRepDisagree,
} from "./lib/aggregations";
import type {
  AIClass,
  DataBundle,
  DateRangePreset,
  Filters,
  OpportunityRow,
  SortState,
  StageState,
  WhoAmI,
} from "./types";
import { familiesOf, isPlatformFamily, splitPlatforms } from "./lib/platform_families";

// Re-export so existing call-sites (kpis.ts, etc.) can keep importing
// from "./state" if they prefer; canonical home is lib/aggregations.
export { TAG_CONFIDENCE_THRESHOLD, classifyAI };

// Active-tab union — must match the data-tab values in index.html. The "ai"
// tab is the new default landing surface (R1.5, replaces "bu" prominence).
//
// State policy (ARCH-05, PR #113): values that belong in AppState are
// (a) cross-tab — read by more than one view, OR (b) URL-persisted —
// part of a shareable bookmark, OR (c) participate in the global
// pub-sub for re-render. View-local ephemeral UI state (picker values,
// section toggles, active sub-views) stays in the view module's own
// scope — see `pickedFrom`/`pickedTo`/`stageViewDetailed` in
// weekly_call.ts and `_activeReport` in reports.ts. Lifting view-local
// state into AppState would force unrelated subscribers to re-render
// on every toggle and would add fields to a shared interface that
// only one view reads.
export type ActiveTab =
  | "ai"
  | "farming"
  | "customers"
  | "hygiene"
  | "lost"
  | "deals"
  | "weekly_call"
  | "partners"
  | "maturity"
  | "admin"
  | "poc_explorer"
  // SGNL-179 — Reports tab for exec-facing PDF distribution.
  | "reports"
  // SGNL-206 — Pipeline Analytics tab wraps Stage Velocity + Funnel
  // as sub-tabs. SGNL-207 removed the standalone "stage_velocity" +
  // "conversion_funnel" entries from this union (and from TAB_KEYS,
  // TAB_RENDERERS, and the nav buttons); their old hashes now redirect
  // here via REDIRECT_MAP in router.ts. The internal renderers are
  // still imported and delegated to from pipeline_analytics.ts's
  // mountSub — they remain reachable as implementation details, just
  // not as top-level ActiveTab values.
  | "pipeline_analytics"
  // SGNL-230 — top-level Revenue Calendar tab. Year-scoped ITB + mutually-
  // exclusive high-prob buckets + per-opp monthly drill + delta-compare.
  // Disambiguated from SGNL-213's `revenue_projection` sub-view inside
  // `pipeline_analytics`: that one is probability-weighted pipeline math
  // ("what's the weighted pipeline worth right now?"); this one is calendar
  // math backed by ingested SF Revenue_Line__c schedule rows ("what month
  // does each dollar recognise?"). See docs/sgnl-229-revenue-projection.md.
  | "revenue_calendar"
  | "placeholder";

interface AppState {
  user: WhoAmI | null;
  data: DataBundle | null;
  filters: Filters;
  activeTab: ActiveTab;
  sort: SortState;
  // SGNL-97 — Operator-toggleable Deal Explorer Tags column visibility.
  // Lives in shared state so re-renders driven by other subscribers
  // preserve the toggle without relying on module-local closure state.
  showTagsColumn: boolean;
  // SGNL-228 — toggle for the pre-qualification cohort (probability < 5%).
  // Default false: KPI strip + analytics tabs show the qualifying+
  // cohort only. When true, the BE `/api/data` call passes
  // `?include_pre_qual=true` to bring back MQL / Pre-Qual / Stage 0
  // deals. Triggers a full bundle re-fetch (the BE filter is the
  // source of truth — FE does NOT re-filter from a larger local set).
  includePreQual: boolean;
}

type Listener = (s: AppState) => void;

/** The default landing tab. Single source of truth — both the initial
 * state AND any fallback (e.g. SGNL-208's non-admin redirect away
 * from #admin) should use this constant so a future product decision
 * to change the default lands in exactly one place. */
export const DEFAULT_ACTIVE_TAB: ActiveTab = "ai";

const state: AppState = {
  user: null,
  data: null,
  filters: {},
  activeTab: DEFAULT_ACTIVE_TAB, // AI breakdown is the default landing tab (R1.5)
  sort: { field: "amount", dir: "desc" },
  showTagsColumn: false,
  includePreQual: false,
};

const listeners: Listener[] = [];

export function getState(): AppState {
  return state;
}

export function subscribe(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
}

function notify(): void {
  for (const fn of listeners) fn(state);
}

export function setUser(user: WhoAmI | null): void {
  state.user = user;
  notify();
}

export function setData(data: DataBundle): void {
  state.data = data;
  notify();
}

export function setFilters(patch: Partial<Filters>): void {
  const next = { ...state.filters, ...patch };
  // Strip empty strings AND empty arrays → undefined to keep filter
  // checks simple. SGNL-249 architect / code-reviewer F-1: an empty
  // array survived the prior `=== "" || undefined` strip and made
  // `countActiveFilters` mis-count `stages: []` as 1 active filter
  // (truthy, !== undefined). Treat `[]` as "no filter" consistently.
  for (const k of Object.keys(next) as (keyof Filters)[]) {
    const v = next[k];
    if (v === "" || v === undefined) delete next[k];
    else if (Array.isArray(v) && v.length === 0) delete next[k];
  }
  // SGNL-205 (architect F-03 / code-reviewer F-06): same-value guard.
  // setFilters is on the hot path — the chip-view restore in main.ts
  // calls it on every hashchange, and idempotent renders should NOT
  // trigger a notify. Without this guard, applyChipView("lost") when
  // state.filters.state is already "lost" still fires notify(), which
  // re-enters renderEverything and creates redundant render cycles.
  if (filtersShallowEqual(state.filters, next)) return;
  state.filters = next;
  syncFiltersToUrl();
  notify();
}

/** Shallow equality for Filters — arrays compared element-wise.
 * Filters has only scalar + string[] fields today, no nested objects. */
function filtersShallowEqual(a: Filters, b: Filters): boolean {
  const aKeys = Object.keys(a) as (keyof Filters)[];
  const bKeys = Object.keys(b) as (keyof Filters)[];
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    const av = a[k];
    const bv = b[k];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length) return false;
      // SGNL-249 architect F-2: sort copies before element-wise
      // compare so `["A","B"]` and `["B","A"]` round-trip equal.
      // Drill-throughs and URL CSV serde can land different orders
      // for the same selection set; without sort, the same-value
      // guard mis-fires and triggers redundant URL writes + renders.
      const as = [...av].sort();
      const bs = [...bv].sort();
      for (let i = 0; i < as.length; i++) if (as[i] !== bs[i]) return false;
    } else if (av !== bv) {
      return false;
    }
  }
  return true;
}

export function setActiveTab(tab: AppState["activeTab"]): void {
  // SGNL-205 (architect F-02): same-value guard. Without it, the
  // outer-guard pattern in main.ts is the only thing preventing render
  // loops on hash-driven boot. Centralising the guard here makes
  // every caller safe.
  if (state.activeTab === tab) return;
  state.activeTab = tab;
  notify();
}

export function setSort(sort: SortState): void {
  state.sort = sort;
  notify();
}

export function setShowTagsColumn(visible: boolean): void {
  state.showTagsColumn = visible;
  notify();
}

export function setIncludePreQual(include: boolean): void {
  // SGNL-228 — toggle ONLY mutates state; the caller is responsible
  // for kicking off the bundle re-fetch + setData() that materialises
  // the new cohort. Keeping the re-fetch out of state.ts avoids a
  // circular dependency between state and api.
  if (state.includePreQual === include) return; // no-op guard
  state.includePreQual = include;
  notify();
}

// Targeted in-place mutation for the admin grid's post-reclassify update.
// Updates the matching opp's classified_at on the bundle so a later
// re-render (e.g. tab-switch back to admin) reflects the just-completed
// classification — without it, the in-memory state stays stale and the
// row's button reverts to "Pull & Classify" when the operator returns
// to the tab. Deliberately does NOT call notify(): the admin tab's
// inline DOM swap (renderInlineResult) has already painted the success
// state, and a global re-render here would clobber the pull-counts
// readout and force every other subscriber to redraw for a single-row
// data change. SGNL-85.
export function updateOppClassifiedAt(oppId: string, isoTimestamp: string): void {
  if (!state.data) return;
  const opp = state.data.opps.find((o) => o.id === oppId);
  if (opp) opp.classified_at = isoTimestamp;
}

// Resolve a date-range preset (and custom from/to) to a concrete window. Returns
// null when no date filter should apply (preset = "all", or unset).
//
// Exported for direct testing. Year-based presets (cy0 = current year, cy1 =
// prior year, cy2 = 2 years prior) are computed against `today` so labels stay
// fresh as time passes.
export function resolveDateRange(
  preset: DateRangePreset | undefined,
  customFrom: string | undefined,
  customTo: string | undefined,
  today: Date,
): { from: string | null; to: string | null } | null {
  if (!preset || preset === "all") return null;
  const yyyy = (d: Date): string => d.toISOString().slice(0, 10);
  const year = today.getUTCFullYear();
  if (preset === "ytd") {
    return { from: `${year}-01-01`, to: yyyy(today) };
  }
  if (preset === "q1") {
    return { from: `${year}-01-01`, to: `${year}-03-31` };
  }
  if (preset === "r12") {
    const from = new Date(today);
    from.setUTCDate(from.getUTCDate() - 365);
    return { from: yyyy(from), to: yyyy(today) };
  }
  if (preset === "cy0") return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (preset === "cy1") return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
  if (preset === "cy2") return { from: `${year - 2}-01-01`, to: `${year - 2}-12-31` };
  // open-cy0: "Active in current year" — close_date >= Jan 1 of this year with NO upper cap.
  // Null close_dates are INCLUDED (deal is open/undated). Closed deals from prior
  // years are excluded. Contrast with cy0 which caps at Dec 31 and excludes nulls.
  if (preset === "open-cy0") return { from: `${year}-01-01`, to: null };
  if (preset === "custom") {
    // Either bound may be empty; null means open-ended on that side.
    return { from: customFrom || null, to: customTo || null };
  }
  return null;
}

// Predicate helpers — exposed so views can build matching drill filters.
// classifyAI is re-exported from lib/aggregations at the top of this file.

export function stageState(r: OpportunityRow): StageState {
  if (r.is_won === true) return "won";
  if (r.is_closed === true && r.is_won === false) return "lost";
  return "open";
}

// Apply current filters to a row collection — pure function, used by views.
export function applyFilters(rows: OpportunityRow[], f: Filters): OpportunityRow[] {
  const dateWindow = resolveDateRange(f.dateRange, f.dateFrom, f.dateTo, new Date());
  // SGNL-26 — precompute the tag filter set once. Empty = no tag filter.
  const tagKeySet = f.tagKeys && f.tagKeys.length > 0 ? new Set(f.tagKeys) : null;
  return rows.filter((r) => {
    if (f.bu && r.bu !== f.bu) return false;
    // SGNL-249 — multi-select stage. Match when ANY selected stage
    // equals the row's stage. Empty array = no filter applied.
    if (
      f.stages &&
      f.stages.length > 0 &&
      (r.stage == null || !f.stages.includes(r.stage))
    )
      return false;
    if (f.type && r.type !== f.type) return false;
    if (f.owner && r.owner_email !== f.owner) return false;
    // SGNL-268 — exact-match opportunity source (e.g. "Anthropic" for ANT).
    if (f.opportunitySource && r.opportunity_source !== f.opportunitySource) return false;
    // Platform tagging — member-level equality, not substring, so "Adobe AEM"
    // never matches "Adobe AEM Assets".
    if (f.platform && !splitPlatforms(r.project_platform).includes(f.platform)) return false;
    if (f.platformFamily && !familiesOf(r.project_platform).includes(f.platformFamily))
      return false;
    if (f.min_amount !== undefined && (r.amount ?? 0) < f.min_amount) return false;
    // SGNL-193 — probability filter. NULL probability excluded when active.
    if (f.probability_min !== undefined) {
      if (r.probability == null || r.probability < f.probability_min) return false;
    }
    // SGNL-209 — upper bound (Focus list 50-90 band defaults).
    if (f.probability_max !== undefined) {
      if (r.probability == null || r.probability > f.probability_max) return false;
    }
    // AI classification + stage state filters — set by drill-throughs from
    // the breakdown tabs, also exposed in the filter bar. SGNL-127 added
    // the LLM-derived variants ('selling-llm' / 'using-llm' / 'disagree')
    // alongside the existing rep variants.
    if (f.aiClass) {
      if (f.aiClass === "disagree") {
        if (!llmRepDisagree(r)) return false;
      } else if (
        f.aiClass === "selling-llm" ||
        f.aiClass === "using-llm" ||
        f.aiClass === "non-llm"
      ) {
        // PR-Agent v2 on PR #59: the Non-AI bucket needs an LLM
        // variant or the AI Breakdown "Non-AI $ (LLM)" drill would
        // route to rep-non rows instead of LLM-non rows. classifyLLMAI
        // returns 'non' for the no-tag case; map it to 'non-llm' for
        // comparison here so the predicate is exhaustive.
        const llmKlass = classifyLLMAI(r);
        const wanted: "selling-llm" | "using-llm" | "non-llm" =
          llmKlass === "non" ? "non-llm" : llmKlass;
        if (wanted !== f.aiClass) return false;
      } else {
        // 'selling' | 'using' | 'non' — rep-side classification.
        if (classifyAI(r) !== f.aiClass) return false;
      }
    }
    if (f.state && stageState(r) !== f.state) return false;
    // Tag filter — opp must carry at least one selected tag with
    // verdict='yes' AND confidence >= threshold. SGNL-26 acceptance:
    // "Selecting Tags=Cloud:AWS shows only opps with that tag verdict
    // at confidence >= threshold."
    if (tagKeySet) {
      const opTags = r.tags ?? [];
      const match = opTags.some(
        (t) =>
          tagKeySet.has(t.tag_key) &&
          t.verdict === "yes" &&
          t.confidence >= TAG_CONFIDENCE_THRESHOLD,
      );
      if (!match) return false;
    }
    // SGNL-153 — health-signal filter. OR semantics: opp qualifies when its
    // active_signals[] contains ANY of the selected slugs. active_signals is
    // populated client-side after the bulk health-signals fetch; an opp whose
    // signals haven't been fetched yet (undefined) is excluded while the filter
    // is active (prevents false "clean" matches during the async hydration).
    if (f.activeSignals && f.activeSignals.length > 0) {
      const wanted = new Set(f.activeSignals);
      const has = new Set(r.active_signals ?? []);
      if (![...wanted].some((s) => has.has(s))) return false;
    }
    // SGNL-29 — "has maturity suggestion" filter, set by clicking
    // a histogram bucket on the AI Maturity tab or the filter-bar
    // checkbox.
    if (f.hasMaturitySuggestion && !r.maturity_suggestion) return false;
    // SGNL-90 — AI Maturity prefix filter from the histogram drill.
    // Boundary check (":" / " " / end) so "Stage 1" doesn't accidentally
    // match "Stage 10:..." in legacy SF picklist values. PR-Agent
    // finding (importance 9) on PR #40.
    if (f.aiMaturity) {
      const rep = r.ai_maturity || "";
      const prefix = f.aiMaturity;
      const matches =
        rep === prefix ||
        rep.startsWith(`${prefix}:`) ||
        rep.startsWith(`${prefix} `);
      if (!matches) return false;
    }
    // Date window filter. Two modes:
    // - Bounded (from + to both set): exclude null close_dates; apply both bounds.
    //   Used by cy0/cy1/ytd/q1 — a deal must confirm it falls in the window.
    // - Lower-bound only (from set, to=null): include null close_dates; only
    //   exclude deals that closed BEFORE the from date. Used by open-cy0 ("Active
    //   in year") — a deal with no close_date is by definition still open/active.
    if (dateWindow !== null) {
      // SGNL-267 — the range applies to close_date by default, or to the
      // authoritative closed_out_date when the operator selects that basis.
      const dateField = f.dateBasis === "closed_out" ? r.closed_out_date : r.close_date;
      const openEnded = dateWindow.to === null;
      if (openEnded) {
        // Include null date (open/undated deals are "active").
        // Exclude only deals definitively dated before the lower bound.
        if (dateField !== null && dateWindow.from && dateField < dateWindow.from) return false;
      } else {
        if (dateField === null) return false;
        if (dateWindow.from && dateField < dateWindow.from) return false;
        if (dateWindow.to && dateField > dateWindow.to) return false;
      }
    }
    if (f.q) {
      const needle = f.q.toLowerCase();
      const hay = [r.name, r.account_name, r.owner_name]
        .filter((x): x is string => Boolean(x))
        .join(" ")
        .toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    // SGNL-265 — account-only substring match. Deliberately does NOT
    // consider opp name / owner, so "nab" finds the account and not the
    // ubiquitous "AI-Enabled" opportunities. A row with no account_name
    // can never match a non-empty account filter.
    if (f.account) {
      const needle = f.account.toLowerCase();
      if (!(r.account_name ?? "").toLowerCase().includes(needle)) return false;
    }
    return true;
  });
}

// ---------- URL <-> filters sync ----------

function syncFiltersToUrl(): void {
  const url = new URL(window.location.href);
  url.search = "";
  for (const [k, v] of Object.entries(state.filters)) {
    if (v === undefined || v === "") continue;
    // Explicit join for array filters (tagKeys, activeSignals) so the
    // serialization intent is clear and a comma-in-slug is a recoverable
    // schema violation rather than a silent round-trip corruption.
    // Architect-review MEDIUM on PR #85.
    const serialized = Array.isArray(v) ? v.join(",") : String(v);
    if (serialized) url.searchParams.set(k, serialized);
  }
  window.history.replaceState({}, "", url.toString());
}

export function readFiltersFromUrl(): void {
  const url = new URL(window.location.href);
  const f: Filters = {};
  const bu = url.searchParams.get("bu");
  if (bu) f.bu = bu;
  // SGNL-249 — accept both new `?stages=A,B,C` and legacy `?stage=X`
  // (single-stage URLs from before multi-select shipped). Legacy
  // form coerces to a 1-element array so existing bookmarks survive.
  const stagesParam = url.searchParams.get("stages");
  const legacyStage = url.searchParams.get("stage");
  // PR-Agent #163 (importance 6): a present-but-empty `?stages=`
  // (e.g. `?stages=,,%20`) should be treated as "not provided" so a
  // valid legacy `?stage=` still wins on read. Otherwise the new
  // param silently nullifies the legacy bookmark.
  let parsedStages: string[] | null = null;
  if (stagesParam != null) {
    const parsed = stagesParam
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    // PR-Agent #163 (importance 5): dedupe to prevent duplicate
    // chips + redundant filter work on URLs like ?stages=A,A.
    // First-seen order preserved so existing assertions stand.
    const seen = new Set<string>();
    const uniq: string[] = [];
    for (const s of parsed) {
      if (seen.has(s)) continue;
      seen.add(s);
      uniq.push(s);
    }
    if (uniq.length > 0) parsedStages = uniq;
  }
  if (parsedStages) {
    f.stages = parsedStages;
  } else if (legacyStage) {
    // architect F-5: trim parity with the new-form CSV split path so a
    // legacy `?stage=+Closed+Won` URL (URL-decoded → " Closed Won")
    // still matches `r.stage === "Closed Won"`.
    const trimmed = legacyStage.trim();
    if (trimmed) f.stages = [trimmed];
  }
  const type = url.searchParams.get("type");
  if (type) f.type = type;
  const owner = url.searchParams.get("owner");
  if (owner) f.owner = owner;
  const q = url.searchParams.get("q");
  if (q) f.q = q;
  // SGNL-265 — account-only search (write side is the generic
  // syncFiltersToUrl serializer; read must be explicit like the others).
  const account = url.searchParams.get("account");
  if (account) f.account = account;
  // Q-03 / T-04: guard numeric URL params against NaN. `Number("foo")` is
  // NaN, which (a) was truthily admitted by the previous `if (raw)` check
  // and (b) makes downstream `<` comparisons silently false — the filter
  // half-works (excludes nulls) but imposes no threshold. Reject
  // non-finite values; treat missing/empty/non-numeric as "no filter".
  const minAmount = url.searchParams.get("min_amount");
  if (minAmount) {
    const n = Number(minAmount);
    if (Number.isFinite(n)) f.min_amount = n;
  }
  // SEC-01 / SEC-05: also clamp to the [0, 100] domain so Infinity /
  // negative values can't no-op the filter via comparison overflow.
  const probabilityMin = url.searchParams.get("probability_min");
  if (probabilityMin) {
    const n = Number(probabilityMin);
    if (Number.isFinite(n) && n >= 0 && n <= 100) f.probability_min = n;
  }
  // SGNL-209 — same domain clamp on the upper bound.
  const probabilityMax = url.searchParams.get("probability_max");
  if (probabilityMax) {
    const n = Number(probabilityMax);
    if (Number.isFinite(n) && n >= 0 && n <= 100) f.probability_max = n;
  }
  const dateRangeParam = url.searchParams.get("dateRange");
  const dateFromParam = url.searchParams.get("dateFrom");
  const dateToParam = url.searchParams.get("dateTo");
  // Back-compat: SGNL-61 renamed `fy25` (fixed 2025 window) to `cy1` (1-year-prior).
  // Old bookmarks with ?dateRange=fy25 would silently drop into "no date filter"
  // since resolveDateRange no longer recognizes the literal. Re-map to a custom
  // 2025 window so the user lands on what they expected.
  if (dateRangeParam === "fy25") {
    f.dateRange = "custom";
    f.dateFrom = "2025-01-01";
    f.dateTo = "2025-12-31";
  } else {
    // Whitelist known presets so unexpected URL values can't silently disable
    // date filtering — caught by PR-Agent on PR #13.
    const ALLOWED: ReadonlySet<DateRangePreset> = new Set([
      "ytd", "q1", "r12", "cy0", "cy1", "cy2", "open-cy0", "all", "custom",
    ]);
    if (dateRangeParam && ALLOWED.has(dateRangeParam as DateRangePreset)) {
      f.dateRange = dateRangeParam as DateRangePreset;
    }
    if (dateFromParam) f.dateFrom = dateFromParam;
    if (dateToParam) f.dateTo = dateToParam;
  }
  // SGNL-267 — date-basis (which date the range targets). Whitelisted; anything
  // else falls through to the "close" default.
  const dateBasisParam = url.searchParams.get("dateBasis");
  if (dateBasisParam === "closed_out") f.dateBasis = "closed_out";
  // SGNL-268 — opportunity source (free-form picklist value, e.g. "Anthropic").
  const oppSource = url.searchParams.get("opportunitySource");
  if (oppSource) f.opportunitySource = oppSource;
  // Platform tagging — shareable links for "all Adobe deals". `platform` is a
  // free-form picklist member; `platformFamily` is whitelisted to known families.
  // CQ-09 — trim, so a hand-edited "?platform=%20Adobe%20AEM" still matches
  // instead of silently returning zero rows.
  const platform = url.searchParams.get("platform")?.trim();
  if (platform) f.platform = platform;
  const platformFamily = url.searchParams.get("platformFamily")?.trim();
  if (platformFamily && isPlatformFamily(platformFamily)) f.platformFamily = platformFamily;
  // Same defensive whitelist for aiClass + state.
  const AI_ALLOWED: ReadonlySet<AIClass> = new Set([
    "selling",
    "using",
    "non",
    // SGNL-127 — LLM variants + Disagree must also pass the URL
    // whitelist so deep-links from the AI Breakdown tab's new subview
    // (?aiClass=disagree, ?aiClass=selling-llm) round-trip correctly.
    "selling-llm",
    "using-llm",
    "non-llm",
    "disagree",
  ]);
  const STATE_ALLOWED: ReadonlySet<StageState> = new Set(["won", "lost", "open"]);
  const aiClass = url.searchParams.get("aiClass");
  if (aiClass && AI_ALLOWED.has(aiClass as AIClass)) f.aiClass = aiClass as AIClass;
  const stateParam = url.searchParams.get("state");
  if (stateParam && STATE_ALLOWED.has(stateParam as StageState)) f.state = stateParam as StageState;
  // SGNL-26 — multi-select tag filter persists as a comma-separated list
  // of tag_keys; threshold as a 0-1 float. Both deep-link from the
  // Partner Opportunities tab so a "drill into AWS" link is shareable.
  const tagKeysParam = url.searchParams.get("tagKeys");
  if (tagKeysParam) {
    const keys = tagKeysParam.split(",").map((k) => k.trim()).filter(Boolean);
    if (keys.length > 0) f.tagKeys = keys;
  }
  // SGNL-153 — health-signal multi-select persists as comma-separated slugs.
  const activeSignalsParam = url.searchParams.get("activeSignals");
  if (activeSignalsParam) {
    const sigs = activeSignalsParam.split(",").map((s) => s.trim()).filter(Boolean);
    if (sigs.length > 0) f.activeSignals = sigs;
  }
  // SGNL-109 — `tagThreshold` URL param is dropped silently. Old deep-links
  // (e.g. ?tagThreshold=0.75) still load — the unknown param is just
  // ignored. Active threshold is TAG_CONFIDENCE_THRESHOLD.
  // SGNL-90 — accept aiMaturity stage-prefix from URL deep-links.
  const aiMaturityParam = url.searchParams.get("aiMaturity");
  if (aiMaturityParam && /^Stage [1-5]$/.test(aiMaturityParam)) {
    f.aiMaturity = aiMaturityParam;
  }
  // Default to current calendar year (Jan 1 – Dec 31) on first load — gives
  // the rep visibility into both closed-this-year work AND open opps with
  // future close dates inside the year. SGNL-78 bug fix: ytd was capping
  // `to` at today, hiding opps with close_date in the future (e.g. opp
  // 006Ph00000wo1luIAA "Tableau Assesment" close=2026-05-18 was invisible
  // when today is 2026-05-07).
  if (!f.dateRange) f.dateRange = "cy0";
  state.filters = f;
  // SGNL-249 test-auto F-1: when the URL carried the legacy
  // `?stage=X` form (and no new `?stages=`), canonicalize back to
  // `?stages=X` so a subsequent share / refresh emits the new form
  // and the legacy param doesn't linger in the address bar.
  if (legacyStage && !stagesParam) syncFiltersToUrl();
}
